import React from "react";
import ReactGA from "react-ga";
import styles from "./list-item.module.scss";
import { useIsMobile } from "../../hooks/mobile.js";

const THEME_ICON_SIZE = 36;
const THEME_ICON_SIZE_MOBILE = 24;
const TOPIC_ICON_SIZE = 24;
const TOPIC_ICON_SIZE_MOBILE = 20;

ReactGA.initialize("UA-11963560-5");

const ICON_KEYWORD = (
    <svg aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
        <path
            fill="currentColor"
            d="M497.941 225.941L286.059 14.059A48 48 0 0 0 252.118 0H48C21.49 0 0 21.49 0 48v204.118a48 48 0 0 0 14.059 33.941l211.882 211.882c18.745 18.745 49.137 18.746 67.882 0l204.118-204.118c18.745-18.745 18.745-49.137 0-67.882zm-22.627 45.255L271.196 475.314c-6.243 6.243-16.375 6.253-22.627 0L36.686 263.431A15.895 15.895 0 0 1 32 252.117V48c0-8.822 7.178-16 16-16h204.118c4.274 0 8.292 1.664 11.314 4.686l211.882 211.882c6.238 6.239 6.238 16.39 0 22.628zM144 124c11.028 0 20 8.972 20 20s-8.972 20-20 20-20-8.972-20-20 8.972-20 20-20m0-28c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48z"
        ></path>
    </svg>
);

const ICON_REEF2050 = (
    <svg aria-hidden="true" focusable="false" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
        <path
            fill="currentColor"
            d="M448 64V12c0-6.6-5.4-12-12-12H140c-6.6 0-12 5.4-12 12v52H12C5.4 64 0 69.4 0 76v61.6C0 199.7 68.1 272 160.7 285.7c29.4 60.7 73.7 90.3 111.3 96.9V480h-86c-14.4 0-26 11.7-26 26.1 0 3.3 2.7 5.9 6 5.9h244c3.3 0 6-2.6 6-5.9 0-14.4-11.6-26.1-26-26.1h-86v-97.4c37.7-6.6 81.9-36.2 111.3-96.9C508 272 576 199.6 576 137.6V76c0-6.6-5.4-12-12-12H448zM32 137.6V96h96v24c0 51.8 7 94.9 18.5 130.2C77.9 232.5 32 178 32 137.6zM288 352c-72 0-128-104-128-232V32h256v88c0 128-56 232-128 232zm256-214.4c0 40.4-46 94.9-114.5 112.6C441 214.9 448 171.8 448 120V96h96v41.6z"
        ></path>
    </svg>
);

export default function ListItem(props) {
    const isMobile = useIsMobile();
    const { opportunity, themes, selectedOpportunity } = props;
    const [expanded, setExpanded] = React.useState(false);

    const primaryTheme = {
        theme: opportunity.themes.primary,
        icon: themes.find(d => d.theme === opportunity.themes.primary).icon,
        child: opportunity.themes.subthemes.filter(d => (d ? d.parent === opportunity.themes.primary : false)),
    };

    const secondaryTheme = opportunity.themes.secondary
        ? {
              theme: opportunity.themes.secondary,
              icon: themes.find(d => d.theme === opportunity.themes.secondary).icon,
              child: opportunity.themes.subthemes.find(d => (d ? d.parent === opportunity.themes.secondary : false)),
          }
        : null;

    const handleExpand = () => {
        setExpanded(!expanded);

        if (expanded) {
            ReactGA.event({
                category: "SCIKNOW: opportunity",
                action: "collapse",
                label: opportunity.short,
            });
        } else {
            ReactGA.event({
                category: "SCIKNOW: opportunity",
                action: "expand",
                label: opportunity.short,
            });
        }
    };

    const handleContact = () => {
        ReactGA.event({
            category: "SCIKNOW: opportunity",
            action: "contact",
            label: opportunity.short,
        });
    };

    React.useEffect(() => {
        if (opportunity && selectedOpportunity) {
            if (selectedOpportunity === opportunity.short) {
                setExpanded(true);
                // scroll to the selected opportunity
                const element = document.querySelector(`[data-opportunity="${opportunity.short}"]`);
                window.scrollTo({ top: element.offsetTop - 100, behaviour: "smooth" });

                ReactGA.event({
                    category: "SCIKNOW: opportunity",
                    action: "scrollTo",
                    label: opportunity.short,
                });
            }
        }
    }, [opportunity, selectedOpportunity]);

    return (
        <div className={styles.listItem} data-opportunity={opportunity.short} data-status={opportunity.status}>
            <div
                className={styles.itemCollapsed}
                onClick={() => {
                    if (opportunity.status !== "Completed") handleExpand();
                }}
            >
                <div className={styles.itemHeadingContainer}>
                    <button className={styles.itemExpando} aria-label={`Open details for ${opportunity.short}`}>
                        {!expanded && (
                            <svg
                                aria-hidden="true"
                                focusable="false"
                                className={styles.chevronDown}
                                role="img"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                            >
                                <path
                                    fill="currentColor"
                                    d="M24.707 38.101L4.908 57.899c-4.686 4.686-4.686 12.284 0 16.971L185.607 256 4.908 437.13c-4.686 4.686-4.686 12.284 0 16.971L24.707 473.9c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L41.678 38.101c-4.687-4.687-12.285-4.687-16.971 0z"
                                ></path>
                            </svg>
                        )}
                        {expanded && (
                            <svg
                                aria-hidden="true"
                                focusable="false"
                                role="img"
                                className={styles.chevronUp}
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 448 512"
                            >
                                <path
                                    fill="currentColor"
                                    d="M441.9 167.3l-19.8-19.8c-4.7-4.7-12.3-4.7-17 0L224 328.2 42.9 147.5c-4.7-4.7-12.3-4.7-17 0L6.1 167.3c-4.7 4.7-4.7 12.3 0 17l209.4 209.4c4.7 4.7 12.3 4.7 17 0l209.4-209.4c4.7-4.7 4.7-12.3 0-17z"
                                ></path>
                            </svg>
                        )}
                    </button>
                    {!isMobile && (
                        <span className={styles.itemTopicIcon}>
                            <img
                                src={"data:image/svg+xml," + encodeURIComponent(opportunity.topic.icon)}
                                alt={opportunity.topic.topic}
                                className={styles.topicIcon}
                                style={{ height: isMobile ? TOPIC_ICON_SIZE_MOBILE : TOPIC_ICON_SIZE, width: "auto" }}
                                aria-hidden="true"
                                height={isMobile ? TOPIC_ICON_SIZE_MOBILE : TOPIC_ICON_SIZE}
                                width="auto"
                            />
                        </span>
                    )}
                    <div className={styles.headingStatusContainer}>
                        <h5 className={styles.itemHeading}>{opportunity.short}</h5>
                        {opportunity.status && (
                            <div className={styles.itemStatus} data-status={opportunity.status}>
                                {opportunity.status}
                            </div>
                        )}
                    </div>
                </div>
                <div className={styles.itemThemesContainer}>
                    {isMobile && (
                        <span className={styles.itemTopicIcon}>
                            <img
                                src={"data:image/svg+xml," + encodeURIComponent(opportunity.topic.icon)}
                                alt={opportunity.topic.topic}
                                className={styles.topicIcon}
                                style={{ height: isMobile ? TOPIC_ICON_SIZE_MOBILE : TOPIC_ICON_SIZE, width: "auto" }}
                                aria-hidden="true"
                                height={isMobile ? TOPIC_ICON_SIZE_MOBILE : TOPIC_ICON_SIZE}
                                width="auto"
                            />
                        </span>
                    )}
                    <span className={styles.itemTheme} data-theme={primaryTheme.theme}>
                        <img
                            src={"data:image/svg+xml," + encodeURIComponent(primaryTheme.icon)}
                            alt={primaryTheme.theme}
                            className={styles.themeIcon}
                            style={{ height: THEME_ICON_SIZE, width: "auto" }}
                            aria-hidden="true"
                            height={THEME_ICON_SIZE}
                            width="auto"
                        />
                    </span>
                    {secondaryTheme && (
                        <span className={styles.itemTheme} data-theme={secondaryTheme.theme}>
                            <img
                                src={"data:image/svg+xml," + encodeURIComponent(secondaryTheme.icon)}
                                alt={secondaryTheme.theme}
                                className={styles.themeIcon}
                                style={{ height: THEME_ICON_SIZE, width: "auto" }}
                                aria-hidden="true"
                                height={THEME_ICON_SIZE}
                                width="auto"
                            />
                        </span>
                    )}
                </div>
            </div>

            {expanded && (
                <div className={styles.itemExpanded}>
                    {opportunity.long && <div className={styles.itemLong}>{opportunity.long}</div>}
                    <div className={styles.itemTags}>
                        <div className={styles.keywords}>
                            {opportunity.keywords.map(k => (
                                <span className={styles.keywordItem} key={k}>
                                    <span className={styles.keywordIcon}>{ICON_KEYWORD}</span>
                                    {k}
                                </span>
                            ))}
                        </div>
                        <div className={styles.reef2050}>
                            <span className={styles.reef2050Label}>Reef 2050 objectives:</span>
                            <span className={styles.reef2050Item}>
                                <span className={styles.reef2050Icon}>{ICON_REEF2050}</span>
                                {opportunity.reef2050}
                            </span>
                        </div>
                    </div>

                    <div className={styles.themes}>
                        <h6>Themes and sub-themes:</h6>
                        <div className={styles.themeItem} data-theme={primaryTheme.theme}>
                            <div className={styles.themeItemTheme}>
                                <span className={styles.themeItemThemeIcon}>
                                    <img
                                        src={"data:image/svg+xml," + encodeURIComponent(primaryTheme.icon)}
                                        alt={primaryTheme.theme}
                                        className={styles.themeIcon}
                                        style={{
                                            height: isMobile ? THEME_ICON_SIZE_MOBILE : THEME_ICON_SIZE,
                                            width: "auto",
                                        }}
                                        aria-hidden="true"
                                    />
                                </span>
                                <span className={styles.themeItemThemeLabel}>{primaryTheme.theme}</span>
                            </div>
                            <div className={styles.themeItemSubtheme}>
                                {primaryTheme.child[0] ? primaryTheme.child[0].value : ""}
                            </div>
                        </div>
                        {secondaryTheme && (
                            <div className={styles.themeItem} data-theme={secondaryTheme.theme}>
                                <div className={styles.themeItemTheme}>
                                    <span className={styles.themeItemThemeIcon}>
                                        <img
                                            src={"data:image/svg+xml," + encodeURIComponent(secondaryTheme.icon)}
                                            alt={secondaryTheme.theme}
                                            className={styles.themeIcon}
                                            style={{ height: THEME_ICON_SIZE, width: "auto" }}
                                            aria-hidden="true"
                                        />
                                    </span>
                                    <span className={styles.themeItemThemeLabel}>{secondaryTheme.theme}</span>
                                </div>
                                <div className={styles.themeItemSubtheme}>
                                    {secondaryTheme.child ? secondaryTheme.child.value : ""}
                                </div>
                            </div>
                        )}
                        {primaryTheme.child.length > 1 && (
                            <div className={styles.themeItem} data-theme={primaryTheme.theme}>
                                <div className={styles.themeItemTheme}>
                                    <span className={styles.themeItemThemeIcon}>
                                        <img
                                            src={"data:image/svg+xml," + encodeURIComponent(primaryTheme.icon)}
                                            alt={primaryTheme.theme}
                                            className={styles.themeIcon}
                                            style={{
                                                height: isMobile ? THEME_ICON_SIZE_MOBILE : THEME_ICON_SIZE,
                                                width: "auto",
                                            }}
                                            aria-hidden="true"
                                        />
                                    </span>
                                    <span className={styles.themeItemThemeLabel}>{primaryTheme.theme}</span>
                                </div>
                                <div className={styles.themeItemSubtheme}>
                                    {primaryTheme.child[1] ? primaryTheme.child[1].value : ""}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.contact}>
                        <h6>Want to find out more?</h6>
                        <button
                            onClick={() => {
                                window.location.href = "mailTo:science@gbrmpa.gov.au";
                                handleContact();
                            }}
                        >
                            Contact our science team
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
